import React, { useCallback, useEffect, useState } from 'react';
import { CalendarClock, KeyRound, Pause, Play, Plus, Trash2 } from 'lucide-react';

import { accesClientsAPI } from '../lib/accesClientsApi';
import { formatDate } from '../lib/formats';
import { libelle, nomLisible } from '../lib/libelles';
import { messageErreur } from '../lib/erreurs';
import { usePagination } from '../hooks/usePagination';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { PageHeader } from '../components/ui/page-header';
import { StatutBadge } from '../components/ui/StatutBadge';
import { BarrePagination } from '../components/ui/pagination-bar';
import { EmptyState } from '../components/ui/empty-state';
import { LoadingState } from '../components/ui/loading-state';
import { toast } from '../components/ui/sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import AccesDialog from '../components/acces/AccesDialog';

const AIDE =
  "Un compte du portail client voit les voyages et les pièces de livraison du client choisi, jamais ceux des autres clients et jamais un montant. L'accès s'arrête à la date de fin, ou dès qu'on le coupe.";

const etendueDe = (acces) => {
  const base = libelle('portail_etendue', acces.scope);
  if (acces.scope === 'camion') {
    const plaques = (acces.truck_labels || []).filter(Boolean).join(', ');
    return plaques ? `${base} : ${plaques}` : base;
  }
  return base;
};

/** Les comptes du portail client : qui voit quoi, jusqu'à quand. */
const AccesClientsPage = () => {
  const [acces, setAcces] = useState([]);
  const [options, setOptions] = useState({ portal_users: [], clients: [], trucks: [] });
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(false);
  const [dialogue, setDialogue] = useState(false);
  const [aModifier, setAModifier] = useState(null);
  const [aRetirer, setARetirer] = useState(null);
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(async () => {
    setErreur(false);
    try {
      const [liste, menus] = await Promise.all([accesClientsAPI.lister(), accesClientsAPI.options()]);
      setAcces(Array.isArray(liste) ? liste : []);
      setOptions(menus);
    } catch (err) {
      setErreur(true);
      toast.error(messageErreur(err, 'Les accès n’ont pas pu être chargés'));
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  const pagination = usePagination(acces);
  const actifs = acces.filter((item) => item.state === 'actif').length;

  const basculer = async (item) => {
    setEnCours(true);
    try {
      await accesClientsAPI.modifier(item.id, { is_active: item.state === 'inactif' });
      toast.success(item.state === 'inactif' ? "L'accès est rouvert" : "L'accès est coupé");
      await charger();
    } catch (err) {
      toast.error(messageErreur(err, "L'accès n'a pas pu être changé"));
    } finally {
      setEnCours(false);
    }
  };

  const retirer = async () => {
    if (!aRetirer) return;
    setEnCours(true);
    try {
      await accesClientsAPI.retirer(aRetirer.id);
      toast.success("L'accès est retiré");
      setARetirer(null);
      await charger();
    } catch (err) {
      toast.error(messageErreur(err, "L'accès n'a pas pu être retiré"));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8" data-testid="acces-clients">
      <PageHeader
        eyebrow="Clients"
        title="Accès clients"
        description={AIDE}
        stats={[
          { label: 'Accès actifs', valeur: actifs, testid: 'acces-actifs' },
          { label: 'Accès donnés', valeur: acces.length },
        ]}
        actions={
          <Button onClick={() => setDialogue(true)} data-testid="acces-donner">
            <Plus className="h-4 w-4" aria-hidden="true" />
            Donner un accès
          </Button>
        }
      />

      {chargement ? (
        <LoadingState shape="table" />
      ) : erreur && acces.length === 0 ? (
        <EmptyState
          variant="error"
          title="Les accès n’ont pas pu être chargés"
          description="Réessayez dans un instant."
          actionLabel="Réessayer"
          onAction={charger}
        />
      ) : acces.length === 0 ? (
        <EmptyState
          icon={KeyRound}
          title="Aucun accès donné"
          actionLabel="Donner un accès"
          onAction={() => setDialogue(true)}
        />
      ) : (
        <Card className="card-base">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Compte</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Ce qu’il voit</TableHead>
                  <TableHead>Fin</TableHead>
                  <TableHead>État</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagination.lignes.map((item) => (
                  <TableRow key={item.id} data-testid={`acces-ligne-${item.id}`}>
                    <TableCell className="font-semibold">
                      {nomLisible(item.donneur_ordre_name, item.donneur_ordre_email, 'Compte supprimé')}
                    </TableCell>
                    <TableCell>{nomLisible(item.client_name, 'Client supprimé')}</TableCell>
                    <TableCell>{etendueDe(item)}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {item.access_expires_at ? formatDate(item.access_expires_at) : 'Sans date'}
                    </TableCell>
                    <TableCell>
                      <StatutBadge domaine="portail_etat" code={item.state} />
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          aria-label="Changer la date de fin"
                          title="Changer la date de fin"
                          onClick={() => setAModifier(item)}
                        >
                          <CalendarClock className="h-4 w-4" aria-hidden="true" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={enCours}
                          aria-label={item.state === 'inactif' ? "Rouvrir l'accès" : "Couper l'accès"}
                          title={item.state === 'inactif' ? "Rouvrir l'accès" : "Couper l'accès"}
                          onClick={() => basculer(item)}
                          data-testid={`acces-basculer-${item.id}`}
                        >
                          {item.state === 'inactif' ? (
                            <Play className="h-4 w-4" aria-hidden="true" />
                          ) : (
                            <Pause className="h-4 w-4" aria-hidden="true" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          aria-label="Retirer l'accès"
                          title="Retirer l'accès"
                          onClick={() => setARetirer(item)}
                          data-testid={`acces-retirer-${item.id}`}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      <BarrePagination pagination={pagination} nom="accès" />

      <AccesDialog
        open={dialogue || Boolean(aModifier)}
        onOpenChange={(ouvert) => {
          if (!ouvert) {
            setDialogue(false);
            setAModifier(null);
          }
        }}
        acces={aModifier}
        options={options}
        onDone={charger}
      />

      <AlertDialog open={Boolean(aRetirer)} onOpenChange={(ouvert) => !ouvert && setARetirer(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retirer cet accès ?</AlertDialogTitle>
            <AlertDialogDescription>
              {aRetirer
                ? `${nomLisible(aRetirer.donneur_ordre_name, 'Ce compte')} ne verra plus les voyages de ${nomLisible(aRetirer.client_name, 'ce client')}.`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={enCours}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              disabled={enCours}
              onClick={(evenement) => {
                evenement.preventDefault();
                retirer();
              }}
              data-testid="acces-retirer-confirmer"
            >
              Retirer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AccesClientsPage;
